// @flow

import styled from '@emotion/styled'
import { navigate } from 'gatsby'
import * as React from 'react'

import { colors, media, variables } from '@toggl/style'
import { useMediaQuery } from '@toggl/ui'

import * as TableOfContent from '../tableOfContent'
import * as text from '../text'

import * as Tabs from './tabs'

type TabType = {
  id: string,
  title: string,
  url: string,
  sections: Array<SectionType>,
}

type SectionType = {
  id: string,
  title: string,
  content: React.Node,
}

type Props = {
  tabs: Array<TabType>,
  initiallyActiveTab: string,
}

export const LegalContent = ({ tabs, initiallyActiveTab }: Props) => {
  const [activeSection, setActiveSection] = React.useState(0)
  const [activeTab] = React.useState(initiallyActiveTab || tabs[0].id)
  const isDesktop = useMediaQuery(media.mqbp[1])
  const tabData: TabType = React.useMemo(
    // $FlowFixMe[incompatible-return]
    (): TabType => tabs.find((tab) => tab.id === activeTab),
    [tabs, activeTab]
  )

  const { sections } = tabData
  const sectionSubTitles = sections?.map((section) => section.title) || []

  React.useEffect(() => {
    const handleScroll = () => {
      const position = window.pageYOffset
      let setSection = 0

      sectionSubTitles.forEach((sub, i) => {
        const id = sections?.[i]?.id || ''
        const elOffsetTop = document.getElementById(id)?.offsetTop
        if (elOffsetTop < position + 20) {
          setSection = i
        }
      })

      if (setSection !== activeSection) {
        setActiveSection(setSection)
      }
    }

    if (isDesktop) {
      window.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }

    return () => {}
  }, [activeSection, isDesktop, sectionSubTitles, sections])

  const onChangeSection = (i) => {
    setActiveSection(i)
    const id = sections?.[i]?.id || ''
    const el = document.getElementById(id)
    if (el) {
      scrollTo(el.offsetTop)
    }
  }

  const onChangeTab = (nextTabId) => {
    const tab = tabs.find((t) => t.id === nextTabId)
    // $FlowFixMe[incompatible-use]
    navigate(tab.url)
  }

  const scrollTo = (top) => {
    window.scroll({
      top,
      behavior: 'smooth',
    })
  }

  return (
    <Root>
      <NavWrapper>
        <Tabs.Wrapper
          tabs={tabs}
          activeTab={activeTab}
          onChangeTab={onChangeTab}
        />
      </NavWrapper>

      <ContentWrapper>
        <TableOfContentWrapper>
          <TableOfContent.Table>
            {sectionSubTitles?.map((entry, i) => (
              <TableOfContent.Entry
                onClick={() => onChangeSection(i)}
                active={activeSection === i}
                key={`tableOfContent_story_${i}`}
              >
                {entry}
              </TableOfContent.Entry>
            ))}
          </TableOfContent.Table>
        </TableOfContentWrapper>

        <SectionsContentWrapper>
          <text.H3 color={colors.beige}>{tabData?.title}</text.H3>

          {tabData?.sections?.map((section) => (
            <SectionStyled key={section.title}>
              <SectionTitle id={section.id}>{section.title}</SectionTitle>
              <SectionContent> {section.content}</SectionContent>
            </SectionStyled>
          ))}
        </SectionsContentWrapper>
      </ContentWrapper>
    </Root>
  )
}

const SectionContent: React.ComponentType<any> = styled.div`
  ${text.paragraph4};
  color: ${colors.beige};
  line-height: 1.75;
  max-width: 750px;
`
const SectionTitle: React.ComponentType<any> = styled(text.H4)`
  color: ${colors.yellow};
  margin: 35px 0 15px;
  ${media.mq[1]} {
    margin: 40px 0 20px;
  }
`

const SectionStyled: React.ComponentType<any> = styled.div`
  ${media.mq[1]} {
    margin-bottom: 45px;
  }
`

const SectionsContentWrapper = styled.div`
  ${media.mq[1]} {
    margin-left: 45px;
  }
`

const TableOfContentWrapper = styled.div`
  display: none;

  ${media.mq[1]} {
    display: block;

    & > div {
      position: sticky;
      top: ${variables.headerHeightTablet}px;
      max-height: calc(95vh - ${variables.headerHeightTablet}px);
      min-height: auto;
      overflow: auto;
    }
  }
  ${media.mq[2]} {
    & > div {
      top: ${variables.headerHeightFull}px;
      max-height: calc(95vh - ${variables.headerHeightFull}px);
    }
  }
`

const ContentWrapper = styled.div`
  display: flex;
  padding: 30px 30px 60px;

  ${media.mq[1]} {
    padding: 54px;
    margin: 0 auto;
  }
`

const NavWrapper = styled.div`
  padding: 20px 25px 0;

  select {
    color: ${colors.beige};
    border-color: ${colors.beige};
  }

  ${media.mq[1]} {
    padding: 0;
  }
`

const Root: React.ComponentType<any> = styled.div`
  display: flex;
  flex-direction: column;

  & p {
    margin-bottom: 15px;
  }

  & table {
    margin: 30px 0;
    width: 100%;
    border: 1px solid ${colors.beige};

    thead {
      text-align: center;
      background: ${colors.beige};
      color: ${colors.darkPurple};
    }

    th,
    td {
      padding: 12px;
    }
  }
`
